import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { getApiBaseUrl, toVancouverDateString, fromDatetimeLocalVancouver } from '../lib/utils';
import { APP_TIMEZONE } from '../config';
import { useAuth } from '../context/AuthContext';
import CondoLayout from './CondoLayout';

const dateFmt = new Intl.DateTimeFormat('en-CA', { timeZone: APP_TIMEZONE });
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dowFmt = new Intl.DateTimeFormat('en-US', { timeZone: APP_TIMEZONE, weekday: 'short' });

function getVancouverDow(date) {
  return DOW.indexOf(dowFmt.format(date));
}

function getDayStart(date) {
  return fromDatetimeLocalVancouver(toVancouverDateString(date) + 'T00:00');
}

function getDayEnd(date) {
  const end = fromDatetimeLocalVancouver(toVancouverDateString(date) + 'T23:59');
  end.setSeconds(59, 999);
  return end;
}

function getMonthStart(date) {
  const dateStr = toVancouverDateString(date);
  const [y, m] = dateStr.split('-').map(Number);
  return fromDatetimeLocalVancouver(`${y}-${String(m).padStart(2, '0')}-01T00:00`);
}

function getMonthEnd(date) {
  const dateStr = toVancouverDateString(date);
  const [y, m] = dateStr.split('-').map(Number);
  const lastDay = new Date(y, m, 0).getDate();
  const end = fromDatetimeLocalVancouver(`${y}-${String(m).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}T23:59`);
  end.setSeconds(59, 999);
  return end;
}

function getGridStart(date) {
  const monthStart = getMonthStart(date);
  const dow = getVancouverDow(monthStart);
  const d = new Date(monthStart.getTime() - dow * 24 * 60 * 60 * 1000);
  return getDayStart(d);
}

function getGridEnd(date) {
  const monthEnd = getMonthEnd(date);
  const dow = getVancouverDow(monthEnd);
  const d = new Date(monthEnd.getTime() + (6 - dow) * 24 * 60 * 60 * 1000);
  return getDayEnd(d);
}

function isSameDay(d1, d2) {
  return dateFmt.format(d1) === dateFmt.format(d2);
}

function formatMonthLabel(date) {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: APP_TIMEZONE });
}

function formatTime(date) {
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: APP_TIMEZONE,
  }).toLowerCase().replace(/\s/g, '');
}

function formatEventTime(event) {
  const start = new Date(event.startTime);
  const end = new Date(event.endTime);
  if (isSameDay(start, end)) {
    return `${formatTime(start)} - ${formatTime(end)}`;
  }
  return 'All day';
}

function formatShortDate(date) {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: APP_TIMEZONE });
}

function formatEventTimeDetailed(event) {
  const start = new Date(event.startTime);
  const end = new Date(event.endTime);
  if (isSameDay(start, end)) {
    return `${formatTime(start)} - ${formatTime(end)}`;
  }
  return `${formatShortDate(start)} ${formatTime(start)} → ${formatShortDate(end)} ${formatTime(end)}`;
}

function formatDayHeading(date) {
  return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: APP_TIMEZONE });
}

function getEventsForDay(events, day) {
  const dayStart = getDayStart(day);
  const dayEnd = getDayEnd(day);
  return events.filter(event => {
    const eventStart = new Date(event.startTime);
    const eventEnd = new Date(event.endTime);
    return eventStart <= dayEnd && eventEnd >= dayStart;
  });
}

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    } else {
      mq.addListener(handler);
      return () => mq.removeListener(handler);
    }
  }, []);

  return isMobile;
}

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function EventCard({ event }) {
  return (
    <div className="bg-primary text-primary-foreground rounded px-1.5 py-0.5 text-[11px] leading-tight overflow-hidden">
      <div className="opacity-80 truncate">{formatEventTime(event)}</div>
      <div className="font-medium truncate">{event.title}</div>
    </div>
  );
}

function DayCell({ day, events, today, currentMonth, onSelect }) {
  const dayEvents = getEventsForDay(events, day);
  const inCurrentMonth = day.getMonth() === currentMonth;
  const isToday = isSameDay(day, today);

  return (
    <button
      type="button"
      onClick={() => onSelect(day)}
      className={`text-left border-r border-b border-border p-1.5 min-h-[110px] flex flex-col gap-1 hover:bg-secondary/60 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset transition-colors ${
        inCurrentMonth ? 'bg-card' : 'bg-secondary/30'
      }`}
    >
      <div
        className={`text-xs font-medium self-start px-1.5 py-0.5 rounded-full ${
          isToday
            ? 'bg-primary text-primary-foreground'
            : inCurrentMonth
              ? 'text-foreground'
              : 'text-muted-foreground'
        }`}
      >
        {day.getDate()}
      </div>
      <div className="flex flex-col gap-1 overflow-hidden">
        {dayEvents.map(event => (
          <EventCard key={event._id} event={event} />
        ))}
      </div>
    </button>
  );
}

function DayModal({ day, events, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!day) return null;
  const dayEvents = getEventsForDay(events, day);

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-foreground">{formatDayHeading(day)}</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground p-1 rounded"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex flex-col gap-3">
          {dayEvents.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No events on this day.</p>
          ) : (
            dayEvents.map(event => (
              <div
                key={event._id}
                className="border border-border rounded-lg p-4 bg-background"
              >
                <div className="text-xs font-medium text-primary mb-1">
                  {formatEventTimeDetailed(event)}
                </div>
                <div className="text-base font-semibold text-foreground mb-1">
                  {event.title}
                </div>
                {event.description && (
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {event.description}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function MonthView({ currentDate, events, today, onSelectDay }) {
  const days = useMemo(() => {
    const start = getGridStart(currentDate);
    const end = getGridEnd(currentDate);
    const list = [];
    const d = new Date(start);
    while (d <= end) {
      list.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    return list;
  }, [currentDate]);

  const currentMonth = currentDate.getMonth();

  return (
    <div className="border-l border-t border-border rounded-lg overflow-hidden bg-card">
      <div className="grid grid-cols-7">
        {WEEKDAY_LABELS.map(label => (
          <div
            key={label}
            className="text-center py-2 border-r border-b border-border text-xs font-semibold text-muted-foreground bg-secondary/40"
          >
            {label}
          </div>
        ))}
        {days.map((day, idx) => (
          <DayCell
            key={idx}
            day={day}
            events={events}
            today={today}
            currentMonth={currentMonth}
            onSelect={onSelectDay}
          />
        ))}
      </div>
    </div>
  );
}

function AgendaEventCard({ event }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-card">
      <div className="text-xs font-medium text-primary mb-1">
        {formatEventTimeDetailed(event)}
      </div>
      <div className="text-lg font-semibold text-foreground">
        {event.title}
      </div>
      {event.description && (
        <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">
          {event.description}
        </p>
      )}
    </div>
  );
}

function AgendaView({ events, agendaStart }) {
  const grouped = useMemo(() => {
    const sorted = [...events].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    const groups = [];
    let currentDayKey = null;
    let currentGroup = null;

    for (const event of sorted) {
      const eventStart = getDayStart(new Date(event.startTime));
      const displayDay = eventStart < agendaStart ? new Date(agendaStart) : eventStart;
      const dayKey = toVancouverDateString(displayDay);
      if (dayKey !== currentDayKey) {
        currentDayKey = dayKey;
        currentGroup = { day: new Date(displayDay), events: [] };
        groups.push(currentGroup);
      }
      currentGroup.events.push(event);
    }
    return groups;
  }, [events, agendaStart]);

  if (grouped.length === 0) {
    return (
      <p className="text-sm text-muted-foreground text-center py-12">
        No upcoming events in the next 3 months.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {grouped.map(({ day, events: dayEvents }) => (
        <div key={day.toISOString()}>
          <div className="text-sm font-semibold text-foreground mb-2 border-b border-border pb-1">
            {formatDayHeading(day)}
          </div>
          <div className="flex flex-col gap-2">
            {dayEvents.map(event => (
              <AgendaEventCard key={event._id} event={event} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function bookingToEvent(booking) {
  const dateStr = new Date(booking.date).toISOString().slice(0, 10);
  return {
    _id: booking._id,
    startTime: fromDatetimeLocalVancouver(`${dateStr}T${booking.startTime}`).toISOString(),
    endTime: fromDatetimeLocalVancouver(`${dateStr}T${booking.endTime}`).toISOString(),
    title: booking.user?.unitRef?.number != null
      ? `Unit ${booking.user.unitRef.number}`
      : (booking.user?.name || 'Unknown'),
    description: ''
  };
}

function AmenityFilter({ amenities, selectedAmenityId, onChange }) {
  if (amenities.length === 0) return null;
  return (
    <select
      value={selectedAmenityId || ''}
      onChange={e => onChange(e.target.value || null)}
      className="px-3 py-2 bg-background border border-border rounded text-foreground text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
    >
      <option value="">General</option>
      {amenities.map(a => (
        <option key={a._id} value={a._id}>{a.name}</option>
      ))}
    </select>
  );
}

export function CalendarContent({ condo }) {
  const { getToken } = useAuth();
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [selectedAmenityId, setSelectedAmenityId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const dateParam = searchParams.get('date');
  const initialDate = dateParam ? new Date(dateParam + 'T12:00:00') : new Date();
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [selectedDay, setSelectedDay] = useState(null);
  const hasNavigated = useRef(false);
  const isMobile = useIsMobile();

  const today = useMemo(() => getDayStart(new Date()), []);
  const gridStart = useMemo(() => getGridStart(currentDate), [currentDate]);
  const gridEnd = useMemo(() => getGridEnd(currentDate), [currentDate]);
  const agendaStart = useMemo(() => today, [today]);
  const agendaEnd = useMemo(() => {
    const d = new Date(today.getTime());
    d.setUTCMonth(d.getUTCMonth() + 3);
    return getDayEnd(d);
  }, [today]);

  const fetchStart = isMobile ? agendaStart : gridStart;
  const fetchEnd = isMobile ? agendaEnd : gridEnd;

  useEffect(() => {
    setSelectedAmenityId(null);
  }, [condo?._id]);

  useEffect(() => {
    if (!condo?._id) return;
    const token = getToken();
    if (!token) return;
    axios.get(`${getApiBaseUrl()}/api/amenities/condo/${condo._id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setAmenities(res.data.amenities || []))
      .catch(() => {});
  }, [condo, getToken]);

  useEffect(() => {
    if (dateParam && !hasNavigated.current) {
      const targetDate = new Date(dateParam + 'T12:00:00');
      if (!isNaN(targetDate.getTime())) {
        setCurrentDate(targetDate);
        hasNavigated.current = true;
      }
    }
  }, [dateParam]);

  useEffect(() => {
    const fetchEvents = async () => {
      if (!condo?._id) return;
      setLoading(true);
      try {
        if (selectedAmenityId) {
          const start = toVancouverDateString(fetchStart);
          const end = toVancouverDateString(fetchEnd);
          const token = getToken();
          const response = await axios.get(
            `${getApiBaseUrl()}/api/amenities/${selectedAmenityId}/bookings/range`,
            { params: { start, end }, headers: { Authorization: `Bearer ${token}` } }
          );
          setEvents((response.data.bookings || []).map(bookingToEvent));
        } else {
          const response = await axios.get(
            `${getApiBaseUrl()}/api/calendar/${condo._id}`,
            { params: { start: fetchStart.toISOString(), end: fetchEnd.toISOString() } }
          );
          setEvents(response.data.events || []);
        }
        setError(null);
      } catch (err) {
        console.error('Failed to fetch calendar events:', err);
        setError('Failed to load calendar events. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [condo, fetchStart, fetchEnd, selectedAmenityId, getToken]);

  const goToToday = () => setCurrentDate(new Date());

  const goPrev = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goNext = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const isCurrentMonth = toVancouverDateString(currentDate).slice(0, 7) === toVancouverDateString(today).slice(0, 7);

  if (loading && events.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        <p className="mt-4 text-muted-foreground">Loading calendar...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/20 border border-destructive text-destructive-foreground px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="md:hidden">
        <AmenityFilter amenities={amenities} selectedAmenityId={selectedAmenityId} onChange={setSelectedAmenityId} />
      </div>

      <div className="hidden md:flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <button
            onClick={goPrev}
            className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
            aria-label="Previous month"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={goNext}
            className="p-2 rounded-lg border border-border hover:bg-secondary transition-colors"
            aria-label="Next month"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        <h2 className="text-lg md:text-xl font-semibold text-foreground order-first md:order-none flex-1 md:text-center">
          {formatMonthLabel(currentDate)}
        </h2>

        <div className="flex items-center gap-2">
          <AmenityFilter amenities={amenities} selectedAmenityId={selectedAmenityId} onChange={setSelectedAmenityId} />
          <button
            onClick={goToToday}
            disabled={isCurrentMonth}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              isCurrentMonth
                ? 'bg-secondary text-muted-foreground cursor-not-allowed'
                : 'bg-primary text-primary-foreground hover:bg-primary/90'
            }`}
          >
            Today
          </button>
        </div>
      </div>

      {isMobile ? (
        <AgendaView events={events} agendaStart={agendaStart} />
      ) : (
        <>
          <MonthView
            currentDate={currentDate}
            events={events}
            today={today}
            onSelectDay={setSelectedDay}
          />
          <DayModal
            day={selectedDay}
            events={events}
            onClose={() => setSelectedDay(null)}
          />
        </>
      )}
    </div>
  );
}

function CalendarPage() {
  return (
    <CondoLayout>
      {({ condo }) => <CalendarContent condo={condo} />}
    </CondoLayout>
  );
}

export default CalendarPage;
